import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { userAgent } from '@tink/tink-link-core/src/user-agent-detector';
import { captureMessage } from '@tink/core/src/sentry';
import { usePageViewEvents } from '../../../../../../../core/Analytics/usePageViewEvents';
import { PageView, UserInteractionEvent } from '../../../../../../../models/analytics';
import { Transfer, TransfersEntity } from '../../../../../../DirectPayments/models/transfer';
import { Credentials, CredentialsEntity } from '../../../../../../../models/credentials';
import { ScreenWrapper } from '../../../../../../../components/atoms/ScreenWrapper/ScreenWrapper';
import { StyledLogo } from '../../../../../../../components/screens/DeviceHandoff/DeviceHandoff.styles';
import {
  messages,
  messages as otherMessages,
} from '../../../../../../../components/screens/DeviceHandoff/messages';
import { Button } from '../../../../../../../components/atoms/Button/Button';
import { LoadingSpinner } from '../../../../../../../components/atoms/LoadingSpinner/LoadingSpinner';
import { AppDispatch, AppState } from '../../../../../../../stores/redux-store';
import { pollTransferForSingleStatusChange as pollTransferForSingleStatusChangeConnect } from '../../../../../services/transfers';
import { setRedirectedCredentialsId as setRedirectedCredentialsIdConnect } from '../../../../../../../stores/app-state-store';
import { signCredentials as signCredentialsConnect } from '../../../../../../../connectivity/services/credentials';
import { useSelectedProvider } from '../../../../../../../components/App/ProviderContainer/useSelectedProvider';
import { ContentBody } from '../../../../../../../components/atoms/ContentBody/ContentBody';
import { Text } from '../../../../../../../components/atoms/Text/Text';

const changeLocation = (newLocation: string) => {
  window.location.href = newLocation;
};

type OpenProviderAppPayload = {
  setLocation?: (newHref: string) => void;
  isAppleDevice: boolean;
  isAndroidDevice: boolean;
  supplementalInformation: string;
};

// introduced temporarly to catch in Sentry the input that should be JSON
const parseJson = (input: string) => {
  try {
    return JSON.parse(input);
  } catch (error) {
    captureMessage('Parsed value is not a correct JSON', {
      custom: {
        parseJsonInput: input,
      },
    });

    return null;
  }
};

const applicationOpener = ({
  supplementalInformation,
  setLocation = changeLocation,
}: OpenProviderAppPayload) => {
  const thirdPartyLinks = parseJson(supplementalInformation);

  if (!thirdPartyLinks) {
    return;
  }

  if (userAgent.isAppleDevice && thirdPartyLinks.ios) {
    setLocation(thirdPartyLinks.ios.deepLinkUrl);
  }

  if (userAgent.isAndroidDevice && thirdPartyLinks.android) {
    setLocation(thirdPartyLinks.android.intent);
  }
};

type HandoffPayCredentialsHandlerProps = PropsFromRedux & {
  openProviderApp?: typeof applicationOpener;
  isAndroidDevice?: boolean;
  isAppleDevice?: boolean;
  isMobileSafari?: boolean;
};

export const HandoffPayCredentialsHandlerPresenter: React.FC<HandoffPayCredentialsHandlerProps> = ({
  credentials,
  transfer,
  pollTransferForSingleStatusChange,
  signCredentials,
  isAndroidDevice = userAgent.isAndroidDevice,
  isAppleDevice = userAgent.isAppleDevice,
  isMobileSafari = userAgent.isMobileSafari,
  openProviderApp = applicationOpener,
}: HandoffPayCredentialsHandlerProps) => {
  const { selectedProvider } = useSelectedProvider();
  const [isButtonVisible, setIsButtonVisible] = useState(false);

  const { sendViewInteractionEvent } = usePageViewEvents({
    pageView: PageView.HANDOFF_PAY_MOBILE_OPEN_APP_SCREEN,
  });

  useEffect(() => {
    // After having created the transfer we want to poll for AWAITING_CREDENTIALS
    if (Transfer.isCreated(transfer)) {
      pollTransferForSingleStatusChange(transfer);
    }

    // Once the transfer is in AWAITING_CREDENTIALS we know we can poll the credentials
    if (Transfer.isAwaiting(transfer)) {
      signCredentials(credentials?.id);
    }
  }, [credentials?.id, pollTransferForSingleStatusChange, signCredentials, transfer]);

  useEffect(() => {
    // If credentials are UPDATED we should poll transfer once
    if (transfer && Transfer.isAwaiting(transfer) && Credentials.isUpdated(credentials)) {
      pollTransferForSingleStatusChange(transfer);
    }
  }, [credentials, pollTransferForSingleStatusChange, transfer]);

  useEffect(() => {
    if (!credentials?.id || !credentials?.supplementalInformation) {
      return;
    }

    if (isMobileSafari) {
      setTimeout(() => {
        openProviderApp({
          supplementalInformation: credentials.supplementalInformation as string,
          isAndroidDevice,
          isAppleDevice,
        });
      }, 1500);

      setTimeout(() => {
        setIsButtonVisible(true);
      }, 4000);
    } else {
      setIsButtonVisible(true);
    }
  }, [
    credentials?.id,
    credentials?.supplementalInformation,
    openProviderApp,
    isAndroidDevice,
    isAppleDevice,
    isMobileSafari,
  ]);

  return (
    <ScreenWrapper>
      <ContentBody centerHorizontally centerVertically>
        <StyledLogo src={selectedProvider.images.icon} />
        <Text bold align="center" my="medium">
          <FormattedMessage
            {...messages.openingApp}
            values={{
              providerName: selectedProvider.displayName,
            }}
          />
        </Text>
        {isButtonVisible ? (
          <Button
            color="primary"
            onClick={() => {
              sendViewInteractionEvent({
                interaction: UserInteractionEvent.PRIMARY_BUTTON,
              });

              openProviderApp({
                supplementalInformation: credentials?.supplementalInformation as string,
                isAndroidDevice,
                isAppleDevice,
              });
            }}
            variant="contained"
            data-testid="open-app-button"
          >
            <FormattedMessage {...otherMessages.open} />
          </Button>
        ) : (
          <LoadingSpinner data-testid="progress-spinner" />
        )}
      </ContentBody>
    </ScreenWrapper>
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data as CredentialsEntity,
    transfer: state.transfer.data as TransfersEntity,
  };
};

/* istanbul ignore next: not testing mapDispatchToProps */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    pollTransferForSingleStatusChange: (transfer: TransfersEntity) => {
      dispatch(pollTransferForSingleStatusChangeConnect(transfer));
    },
    setRedirectedCredentialsId: (id: string) => {
      dispatch(setRedirectedCredentialsIdConnect(id));
    },
    signCredentials: (credentialsId: string) => {
      signCredentialsConnect({ credentialsId, dispatch });
    },
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const HandoffPayCredentialsHandler = connector(HandoffPayCredentialsHandlerPresenter);
